import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GameLevel, PlayerAssignment } from '../../types/game';
import { OrnamentDivider } from '../ui/OrnamentDivider';
import { HintList } from './HintList';
import { RoleCounter } from './RoleCounter';

interface ZoneCInfoProps {
  level: GameLevel;
  assignments: PlayerAssignment[];
  checkedHints: string[];
  onToggleHint: (id: string) => void;
  visibleDay: number;
  onCheckSolution: () => void;
  onReset: () => void;
  walkthrough?: string;
  onShowSolution?: () => void;
}

export function ZoneC_Info({
  level,
  assignments,
  checkedHints,
  onToggleHint,
  visibleDay,
  onCheckSolution,
  onReset,
  walkthrough,
  onShowSolution,
}: ZoneCInfoProps) {
  const { t } = useTranslation('game');
  const [walkthroughOpen, setWalkthroughOpen] = useState(false);
  const greyCount = assignments.filter((a) => a.role === 'grey').length;

  function handleShowSolution() {
    setWalkthroughOpen(true);
    onShowSolution?.();
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Brief */}
      <div>
        <h3 className="font-mono text-[10px] uppercase tracking-widest text-mafia-muted mb-2">
          {t('board.zones.brief')}
        </h3>
        <p className="text-xs text-mafia-paper-dim leading-relaxed whitespace-pre-line">
          {level.brief}
        </p>
      </div>

      <OrnamentDivider variant="dim" />

      {/* Hints */}
      <HintList
        hints={level.hints}
        checkedHints={checkedHints}
        onToggleHint={onToggleHint}
        visibleDay={visibleDay}
      />

      <OrnamentDivider variant="dim" />

      {/* Role counter */}
      <RoleCounter assignments={assignments} />

      <OrnamentDivider variant="dim" />

      {/* Actions */}
      <div className="flex flex-col gap-2">
        <button
          onClick={onCheckSolution}
          disabled={greyCount > 0}
          className={`w-full py-2.5 text-xs font-mono tracking-widest uppercase transition-colors ${
            greyCount > 0 ? 'btn-gold opacity-40 cursor-not-allowed' : 'btn-gold'
          }`}
        >
          {t('board.check')}
        </button>

        {walkthrough && !walkthroughOpen && (
          <button
            onClick={handleShowSolution}
            className="w-full py-2 text-xs font-mono tracking-widest uppercase text-mafia-crimson/60 hover:text-mafia-crimson transition-colors border border-mafia-crimson/20 rounded hover:border-mafia-crimson/40"
          >
            Показать решение
          </button>
        )}

        <button
          onClick={onReset}
          className="w-full py-2 text-xs font-mono tracking-widest uppercase text-mafia-muted hover:text-mafia-paper-dim transition-colors"
        >
          {t('board.reset')}
        </button>
      </div>

      {/* Walkthrough (revealed on demand) */}
      {walkthroughOpen && walkthrough && (
        <>
          <OrnamentDivider variant="dim" />
          <div className="flex flex-col gap-2">
            <h4 className="font-mono text-[10px] uppercase tracking-widest text-mafia-crimson/70">
              Ход решения
            </h4>
            <p className="text-xs text-mafia-paper-dim leading-relaxed whitespace-pre-line">
              {walkthrough}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
